import React, { useCallback, useEffect, useState } from "react";
import { Download, ExternalLink, FileText, RefreshCw } from "lucide-react";
import Button from "../forms/Button";
import { sprintService, apiErrorMessage } from "../../api/taskBoardService";
import { isSupabaseStorageAvailable } from "../../api/supabaseStorage";
import { SectionCard, ErrorState, SkeletonRows } from "./shared";
import { STATUS_LABELS } from "./constants";
import { buildRetrospectivePdf, downloadBlob, fmtDate, saveSprintDocument } from "./sprintDocuments";

const NOTES = [
  ["wentWell", "What went well"],
  ["wentWrong", "What went wrong"],
  ["blockers", "Blockers"],
  ["improvements", "Improvement notes"],
];
const emptyNotes = { wentWell: "", wentWrong: "", blockers: "", improvements: "" };

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-line p-3">
    <p className="text-xs text-faint">{label}</p>
    <p className="text-xl font-semibold text-ink">{value}</p>
  </div>
);

const Stats = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
    <Stat label="Completed tasks" value={stats.completedTasks} />
    <Stat label="Incomplete tasks" value={stats.incompleteTasks} />
    <Stat label="Total tasks" value={stats.totalTasks} />
    <Stat label="Completion rate" value={`${stats.completionRate}%`} />
  </div>
);

const NotesForm = ({ value, onChange, disabled }) => (
  <div className="space-y-3">
    {NOTES.map(([key, label]) => (
      <label key={key} className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-gray-700 dark:text-light-200">{label}</span>
        <textarea
          className="input-field w-full min-h-[84px]"
          maxLength={5000}
          disabled={disabled}
          value={value[key]}
          onChange={(e) => onChange({ ...value, [key]: e.target.value })}
        />
      </label>
    ))}
  </div>
);

/**
 * Sprint Retrospective tab. Completing a sprint shows the form: the numbers
 * are the API's live count from the tasks' real statuses (and are frozen by the
 * API when the sprint is completed, never taken from the browser). The saved
 * retrospective can be exported as a PDF kept in the sprint's documents.
 */
const SprintRetrospectiveTab = ({ sprint, admin, refreshKey, onSprintChange, onCompleted, showError, showSuccess }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState(emptyNotes);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = (await sprintService.retrospective(sprint._id)).data;
      setData(res);
      setError("");
      return res;
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load the retrospective"));
      return null;
    }
  }, [sprint._id]);

  // Don't overwrite what is being typed when realtime refreshes the numbers.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const makePdf = async (view) => {
    const updated = await saveSprintDocument({
      sprint,
      kind: "retrospective",
      blob: buildRetrospectivePdf(view),
      sourceHash: view.hash,
      label: "Retrospective",
    });
    onSprintChange(updated);
  };

  const guarded = async (fn, failure) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      showError(apiErrorMessage(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const complete = () =>
    guarded(async () => {
      if (!window.confirm("Complete this sprint? Its numbers are frozen and unfinished tasks stay linked to it until you move them.")) return;
      const res = (await sprintService.complete(sprint._id, notes)).data;
      setData(res);
      onCompleted();
      showSuccess("Sprint completed");
      try {
        await makePdf(res);
        showSuccess("Retrospective report saved to Documents");
        await load();
      } catch (err) {
        showError(`Retrospective saved, but its PDF was not: ${apiErrorMessage(err, "upload failed")}. Use “Generate & save”.`);
      }
    }, "Could not complete the sprint");

  const saveNotes = () =>
    guarded(async () => {
      const res = (await sprintService.updateRetrospective(sprint._id, notes)).data;
      setData(res);
      setEditing(false);
      showSuccess("Retrospective updated. Regenerate the PDF to include the changes.");
    }, "Could not save the changes");

  const regenerate = () =>
    guarded(async () => {
      const fresh = await load();
      await makePdf(fresh);
      showSuccess("Retrospective report saved to Documents");
      await load();
    }, "Could not generate the report");

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <SkeletonRows rows={3} />;

  const { retrospective: retro, live, document: doc } = data;

  // ---- Not completed yet ----
  if (!retro) {
    if (!data.canComplete) {
      return (
        <SectionCard title="Sprint retrospective">
          <p className="text-sm italic text-faint">
            {data.sprint.status === "planned"
              ? "The retrospective is available once the sprint has started."
              : "No retrospective has been recorded for this sprint."}
          </p>
        </SectionCard>
      );
    }
    return (
      <SectionCard title="Complete sprint & retrospective">
        <p className="text-xs text-faint mb-3">The numbers below are counted from the tasks' current statuses.</p>
        <Stats stats={live} />
        {live.incompleteList.length > 0 && (
          <div className="mt-3">
            <p className="text-sm font-medium text-ink mb-1">Incomplete tasks (will stay linked to this sprint)</p>
            <ul className="text-sm space-y-1">
              {live.incompleteList.map((t) => (
                <li key={t.task}>
                  <span className="font-mono text-[11px] text-faint">#{t.number}</span> {t.title} — <span className="text-muted">{STATUS_LABELS[t.status]}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-4">
          <NotesForm value={notes} onChange={setNotes} disabled={busy} />
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={complete} disabled={busy}>
            {busy ? "Completing…" : "Complete sprint"}
          </Button>
        </div>
      </SectionCard>
    );
  }

  // ---- Retrospective report ----
  return (
    <div className="space-y-4">
      <SectionCard
        title="Retrospective report"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => downloadBlob(buildRetrospectivePdf(data), `${sprint.name.replace(/[^\w-]+/g, "-")}_Retrospective.pdf`)}>
              <Download className="w-4 h-4" /> Download PDF
            </Button>
            {admin && (
              <Button size="sm" onClick={regenerate} disabled={busy || !isSupabaseStorageAvailable()}>
                <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
                {doc ? "Regenerate & save" : "Generate & save to Documents"}
              </Button>
            )}
          </div>
        }
      >
        <p className="text-xs text-faint mb-3">Completed {fmtDate(retro.completedAt)}. These numbers were frozen when the sprint was completed.</p>
        <Stats stats={retro.stats} />
        <div className="mt-3 text-sm">
          {doc ? (
            <span className="inline-flex flex-wrap items-center gap-2">
              <FileText className="w-4 h-4 text-faint" />
              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-primary-accent hover:underline inline-flex items-center gap-1">
                {doc.name} <ExternalLink className="w-3 h-3" />
              </a>
              {doc.stale ? <span className="pill pill-warn">Outdated — notes changed</span> : <span className="pill pill-ok">Up to date</span>}
            </span>
          ) : (
            <span className="italic text-faint">No PDF saved yet.</span>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Notes"
        actions={
          admin &&
          (editing ? (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
              <Button size="sm" onClick={saveNotes} disabled={busy}>Save</Button>
            </div>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setNotes(Object.fromEntries(NOTES.map(([k]) => [k, retro[k] || ""])));
                setEditing(true);
              }}
            >
              Edit notes
            </Button>
          ))
        }
      >
        {editing ? (
          <NotesForm value={notes} onChange={setNotes} disabled={busy} />
        ) : (
          <div className="space-y-3">
            {NOTES.map(([key, label]) => (
              <div key={key}>
                <p className="text-sm font-medium text-ink">{label}</p>
                <p className={`text-sm whitespace-pre-wrap break-words ${retro[key] ? "text-muted" : "italic text-faint"}`}>{retro[key] || "Nothing noted."}</p>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title={`Incomplete tasks at completion (${retro.incompleteList?.length || 0})`}>
        {retro.incompleteList?.length ? (
          <ul className="text-sm space-y-1">
            {retro.incompleteList.map((t) => (
              <li key={t.task}>
                <span className="font-mono text-[11px] text-faint">#{t.number}</span> {t.title} — <span className="text-muted">{STATUS_LABELS[t.status]}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm italic text-faint">Every task was completed.</p>
        )}
      </SectionCard>
    </div>
  );
};

export default SprintRetrospectiveTab;
